import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import type { GetTemplateFolder, GetTemplateItem } from "./templatesTypes";
import type { TemplateBuilderCategory } from "./templatesTypes";
import { toActiveVersionLabel } from "../../utils/templateVersionLabel";
import { isDraftsFolderName, toDisplayFolderName } from "../../utils/folderNames";

type FilterMode = "all" | "draft" | "published";

const normalizeBoolean = (value: unknown): boolean => {
  if (typeof value === "string") {
    return ["true", "y", "yes", "1"].includes(value.toLowerCase());
  }

  return value === true || value === 1;
};

export const getPublishedVersion = (template: GetTemplateItem) =>
  template.versions.find(
    (version) =>
      version.templateVersionNumber !== null &&
      normalizeBoolean(version.isActive),
  );

export const hasPublishedVersion = (template: GetTemplateItem): boolean =>
  Boolean(getPublishedVersion(template));

export const filterTemplatesByMode = (
  templates: GetTemplateItem[] | undefined,
  mode: FilterMode = "all",
): GetTemplateItem[] => {
  if (!templates) return [];

  return templates.filter((template) => {
    if (!template.versions || template.versions.length === 0) return false;

    const templateState = template.templateState.toUpperCase();
    const isActive = template.isActive === "Y";
    const hasNullVersionNumber = template.versions.some(
      (version) => version.templateVersionNumber === null,
    );

    if (!isActive) return false;
    if (mode === "draft") return templateState === "DRAFT";
    if (mode === "published") {
      return templateState === "PUBLISHED" && hasNullVersionNumber;
    }

    return (
      templateState === "DRAFT" ||
      (templateState === "PUBLISHED" && hasNullVersionNumber)
    );
  });
};

/**
 * Unified function to transform template folders to tree data
 * @param folders - Template folders to transform
 * @param mode - Filter mode: 'all' (draft+published), 'draft' only, or 'published' only
 * @param restrictToDraftFolder - If true (used for draft mode), only include the Drafts folder
 */
export const transformFoldersToTreeData = (
  folders: GetTemplateFolder[] | undefined,
  mode: FilterMode = "all",
  restrictToDraftFolder: boolean = false,
): TemplateBuilderCategory[] => {
  if (!folders || folders.length === 0) {
    return [];
  }

  // Apply folder restriction for draft mode
  let foldersToProcess = folders;
  if (restrictToDraftFolder) {
    foldersToProcess = folders.filter((folder) =>
      isDraftsFolderName(folder.folderName),
    );
  }

  const sortedFolders = [...foldersToProcess].sort((a, b) => {
    const isDraftsA = isDraftsFolderName(a.folderName);
    const isDraftsB = isDraftsFolderName(b.folderName);
    if (isDraftsA) return -1;
    if (isDraftsB) return 1;
    return a.folderName.localeCompare(b.folderName);
  });
  const result = sortedFolders
    .map((folder) => {
      const validTemplates = filterTemplatesByMode(folder.templates, mode);

      const sortedTemplates = [...validTemplates].sort((a, b) =>
        a.templateName.localeCompare(b.templateName),
      );

      return {
        id: folder.folderName,
        label: toDisplayFolderName(folder.folderName),
        templates: sortedTemplates.map((template) => ({
          id: template.templateId,
          label: template.templateName,
          stat: hasPublishedVersion(template) ? "PUBLISHED" : "DRAFT",
          templatePriority: template.templatePriority,
        })),
      } as const;
    })
    .filter((folder) => folder.templates.length > 0);

  return result;
};

export const selectTemplatesLoading = (state: RootState) =>
  state.templates.loading;

export const selectTemplatesError = (state: RootState) => state.templates.error;

// Selector to get category-based cached data
export const selectDataByCategory = (state: RootState) =>
  state.templates.dataByCategory;

const getActiveTemplateFolders = (
  dataByCategory: ReturnType<typeof selectDataByCategory>,
  accountId: string | undefined,
  clientId: string | undefined,
): GetTemplateFolder[] => {
  const hasAccountId = Boolean(accountId);
  const hasClientId = Boolean(clientId);

  if (hasAccountId && hasClientId) {
    return dataByCategory.CLIENT?.clientTemplates ?? [];
  }

  if (hasAccountId) {
    const source = dataByCategory.PLATFORM || dataByCategory.CLIENT;
    return source?.platformTemplates ?? [];
  }

  return dataByCategory.OPTUM?.globalTemplates ?? [];
};

// Memoized helper selector to get all template folders across all category caches
export const selectAllTemplateFolders = createSelector(
  [selectDataByCategory],
  (dataByCategory) => {
    const allFolders: GetTemplateFolder[] = [];

    if (dataByCategory.OPTUM) {
      allFolders.push(...(dataByCategory.OPTUM.globalTemplates || []));
      allFolders.push(...(dataByCategory.OPTUM.platformTemplates || []));
      allFolders.push(...(dataByCategory.OPTUM.clientTemplates || []));
    }

    if (dataByCategory.PLATFORM) {
      allFolders.push(...(dataByCategory.PLATFORM.globalTemplates || []));
      allFolders.push(...(dataByCategory.PLATFORM.platformTemplates || []));
      allFolders.push(...(dataByCategory.PLATFORM.clientTemplates || []));
    }

    if (dataByCategory.CLIENT) {
      allFolders.push(...(dataByCategory.CLIENT.globalTemplates || []));
      allFolders.push(...(dataByCategory.CLIENT.platformTemplates || []));
      allFolders.push(...(dataByCategory.CLIENT.clientTemplates || []));
    }

    return allFolders;
  },
);

// Selector to get template versions mapped by templateId in the format: { id, label, value }
// "Draft" is always included as the first entry so the user can return to the editable draft at any time.
export const selectTemplateVersionsByTemplateId = createSelector(
  [selectAllTemplateFolders],
  (allFolders) => {
    const versionsByTemplateId: Record<
      string,
      Array<{ id: string; label: string; value: string }>
    > = {};

    allFolders.forEach((folder) => {
      folder.templates.forEach((template) => {
        const publishedVersions = (template.versions || [])
          .filter((version) => version.templateVersionNumber !== null)
          .sort(
            (a, b) =>
              (b.templateVersionNumber || 0) - (a.templateVersionNumber || 0),
          ) // Sort descending
          .map((version) => {
            const versionNum = version.templateVersionNumber!;
            const versionLabel = `V${versionNum}`;
            const versionIsActive = version.isActive === "Y";
            const label = versionIsActive
              ? toActiveVersionLabel(versionLabel)
              : versionLabel;
            return {
              id: String(versionNum),
              label,
              value: versionLabel,
            };
          });

        // Always prepend "Draft" so users can return to the editable draft version
        const draftEntry = { id: "draft", label: "Draft", value: "Draft" };
        versionsByTemplateId[template.templateId] = [
          draftEntry,
          ...publishedVersions,
        ];
      });
    });

    return versionsByTemplateId;
  },
);

export const selectVersionIdMapByTemplateId = createSelector(
  [selectAllTemplateFolders],
  (allFolders) => {
    const versionIdMap: Record<string, Record<string, number>> = {};

    allFolders.forEach((folder) => {
      folder.templates.forEach((template) => {
        const idMap: Record<string, number> = {};

        (template.versions || []).forEach((version) => {
          if (version.templateVersionNumber === null) {
            idMap["Draft"] = version.templateVersionId;
          } else {
            idMap[`V${version.templateVersionNumber}`] =
              version.templateVersionId;
          }
        });

        versionIdMap[template.templateId] = idMap;
      });
    });

    return versionIdMap;
  },
);

/**
 * Selector to get active folders based on current account/client context.
 * Mirrors the tree view logic - returns folders that should be visible in the tree.
 * This centralizes the filtering logic used by both EmailTemplate and BaseTemplateFormModal.
 * Only includes folders that have at least one valid template (same as tree view).
 */
export const selectActiveFolderNames = createSelector(
  [
    selectDataByCategory,
    (state: RootState) => state.metadata.accountId,
    (state: RootState) => state.metadata.clientId,
  ],
  (dataByCategory, accountId, clientId) => {
    const folders = getActiveTemplateFolders(
      dataByCategory,
      accountId,
      clientId,
    );

    // Apply the same filtering as transformFoldersToTreeData to only include folders with valid templates
    const treeData = transformFoldersToTreeData(folders);
    return treeData.map((category) => category.label);
  },
);

// Selector for the breadcrumb category label shown above the active folder's templates.
export const selectActiveTemplateCategoryLabel = createSelector(
  [
    (state: RootState) => state.metadata.accountId,
    (state: RootState) => state.metadata.clientId,
  ],
  (accountId, clientId): string => {
    const hasAccountId = Boolean(accountId);
    const hasClientId = Boolean(clientId);

    if (hasAccountId && hasClientId) return "Client Template";
    if (hasAccountId) return "Platform Template";
    return "Global Template";
  },
);

export const selectActiveTemplateFolders = createSelector(
  [
    selectDataByCategory,
    (state: RootState) => state.metadata.accountId,
    (state: RootState) => state.metadata.clientId,
  ],
  (dataByCategory, accountId, clientId): GetTemplateFolder[] =>
    getActiveTemplateFolders(dataByCategory, accountId, clientId)
      .map((folder) => ({
        ...folder,
        templates: filterTemplatesByMode(folder.templates),
      }))
      .filter((folder) => folder.templates.length > 0),
);
